import * as React from 'react';

import { useContainerBreakpoint } from '../../../hooks/useContainerBreakpoint';
import { cx } from '../../../utils/cx';
import { toCurrency } from '../../../utils/formatting';

export interface ISpendingChartHeaderProps {
  title: string;
  subtitle?: string;
  netTotal?: number;
  income?: number;
  spending?: number;
  className?: string;
}

export const SpendingChartHeader = ({
  title,
  subtitle,
  netTotal,
  income,
  spending,
  className = '',
}: ISpendingChartHeaderProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const useMobileLayout = !useContainerBreakpoint(containerRef, 'md');

  return (
    <div ref={containerRef} className={cx('bg-secondary flex flex-col gap-5', className)}>
      <div className="flex flex-col gap-5 px-5 pt-5">
        {/* Mobile Layout */}
        {useMobileLayout && (
          <div className="flex flex-col gap-1">
            {/* Row 1: Title (left) and Net Total (right) */}
            <div className="flex items-center justify-between">
              <span className="text-primary text-lg font-semibold">{title}</span>
              {netTotal !== undefined && (
                <span
                  className={cx('text-xl font-semibold', netTotal >= 0 ? 'text-success-primary' : 'text-error-primary')}
                >
                  {netTotal >= 0 ? '+' : ''}
                  {toCurrency(netTotal)}
                </span>
              )}
            </div>

            {/* Row 2: Subtitle */}
            {subtitle && <span className="text-tertiary text-xs font-medium">{subtitle}</span>}
          </div>
        )}

        {/* Desktop Layout */}
        {!useMobileLayout && (
          <div className="flex w-full items-center justify-between gap-4">
            {/* Title */}
            <div className="flex flex-col gap-1">
              <span className="text-primary text-lg font-semibold">{title}</span>
              {subtitle && <span className="text-tertiary text-xs font-medium">{subtitle}</span>}
            </div>

            {/* Right side: Stats */}
            <div className="flex items-end gap-3">
              {/* Net Total */}
              {netTotal !== undefined && (
                <div className="flex flex-col gap-0.5">
                  <div className="text-quaternary text-[10px] font-semibold tracking-wide">Net total</div>
                  <div
                    className={cx(
                      'text-lg font-semibold',
                      netTotal >= 0 ? 'text-success-primary' : 'text-error-primary'
                    )}
                  >
                    {netTotal >= 0 ? '+' : ''}
                    {toCurrency(netTotal)}
                  </div>
                </div>
              )}

              {/* Equals sign */}
              {netTotal !== undefined && income !== undefined && (
                <div className="text-tertiary pb-0.5 text-lg font-semibold">=</div>
              )}

              {/* Income */}
              {income !== undefined && (
                <div className="flex flex-col gap-0.5">
                  <div className="text-quaternary text-[10px] font-semibold tracking-wide">Inflow</div>
                  <div className="text-primary text-lg font-semibold">{toCurrency(income)}</div>
                </div>
              )}

              {/* Minus sign */}
              {income !== undefined && spending !== undefined && (
                <div className="text-tertiary pb-0.5 text-lg font-semibold">-</div>
              )}

              {/* Spending */}
              {spending !== undefined && (
                <div className="flex flex-col gap-0.5">
                  <div className="text-quaternary text-[10px] font-semibold tracking-wide">Spending</div>
                  <div className="text-primary text-lg font-semibold">{toCurrency(spending)}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="bg-utility-gray-100 h-px w-full" />
    </div>
  );
};
